import { getProductInfo, selectItem } from '@/utils/analytics.js';
import cn from 'classnames';
import Link from 'next/link';
import React, { useRef, useEffect, useState, useContext, useCallback } from 'react';
import { useRouter } from 'next/router';
import {
  SnackBar,
  WaveBreadCrumb,
  ListProductCatalogueHeader,
  OffersSnackbar,
  ListsProductCatalogueV2,
  ProductCatalogueV2,
} from '@desquared/wave-components-library';
import { groupProductsByCategory } from '@/utils/category';
import { checkListStatus, hasCommonCollection } from '@/utils/lists';
import {
  addQuantityLimit,
  checkProductAvailability,
  getProductBasedOnSlug,
  getProductIndexBasedOnSlug,
  getProductPropsWithCleanUrl,
  initMyoProducts,
} from '@/utils/products';
import { openListSideSheet, addProduct, removeProduct, getBundleOfferDescription } from '@/utils/products';
import { ResponsiveMenuStore, BundleOfferSideSheetStore } from '@/store/sideSheet';
import { BundleOfferSnackBarOpen } from '@/store/bundleOfferSnackBar';
import { SelectedBundleOfferStore } from '@/store/selectedBundleOfferId';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext.jsx';
import { useTranslations } from 'next-intl';
import EmptyState from '../../../public/assets/wave-grocery/Empty-States-Default/Favorites.png';

import styles from '@/styles/Categories.module.css';
import { CurrencyContext } from '@/contexts/CurrencyProvider';
import { updateCartOrder } from '@/utils/products/products';
import { productsForOrder } from '@/store/cart';
import { ModalContext } from '@/contexts/ModalProvider';
import { useQueryClient } from '@tanstack/react-query';
import { filterObjectsByUniqueSKU } from '@/utils/lists';
import { enhanceProductWithSlug } from '@/utils/products/productSlugs';
import { getProductsWithCategory } from '@/utils/category';
import useProductTags from '@/hooks/useProductTags';
import LoaderStore from '@/store/loader';
import CustomImage from '../CustomImage/CustomImage';

let TIMER = undefined;
const SCROLL_THRESHOLD = 400;

export default function ListCatalogue({
  products,
  handleLoadMore,
  isFetchingNextPage,
  isLoading,
  limitations,
  numberOfProducts,
  selectedList,
  bundleOffersSummary,
  image,
  genericErrorImage,
  defaultProductImage,
  fetchAllProducts,
  shop,
}) {
  const queryClient = useQueryClient();
  const observer = useRef(null);
  const { productTags } = useProductTags();
  const [showSnackBar, setShowSnackbar] = useState(false);
  const [showBundleOffersSnackBar, setShowBundleOffersSnackBar] = useState(false);
  const { formatter } = useContext(CurrencyContext);
  const t = useTranslations('common');
  const t1 = useTranslations('user_lists');
  const { openActionModal, close } = useContext(ModalContext);

  const lastResultItemRef = useCallback(
    node => {
      if (!node) return;
      if (isLoading || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
        }
      });

      observer.current.observe(node);
    },
    [isLoading, isFetchingNextPage, handleLoadMore]
  );
  const router = useRouter();

  useEffect(() => {
    const BundleOfferSnackBarOpen$ = BundleOfferSnackBarOpen.subscribe(setShowBundleOffersSnackBar);

    return () => {
      BundleOfferSnackBarOpen$.unsubscribe();
    };
  }, []);

  const openMenu = () => {
    ResponsiveMenuStore.open();
  };
  const groupedProducts = groupProductsByCategory(products, null);
  addQuantityLimit(groupedProducts, setShowSnackbar, limitations);
  const breadCrumbItems = [{ title: t('breadcrumb_title'), link: '/user/lists' }];
  const { getCollectionTypes } = useContext(DeliveryServicesContext);

  const checkProductCollectionAndAvailability = product => {
    const selectedCollectionTypes = getCollectionTypes();
    if (!hasCommonCollection(product.collectionTypes, selectedCollectionTypes)) {
      return false;
    } else {
      return checkProductAvailability(product);
    }
  };

  const addProductsToCart = () => {
    let allItems = queryClient.getQueryData(['lists', 'list', router.query.id, shop]).pages.flatMap(page => {
      return page.data.data;
    });
    allItems = filterObjectsByUniqueSKU(allItems, getCollectionTypes()).map(enhanceProductWithSlug);

    allItems = groupProductsByCategory(
      getProductsWithCategory(allItems, undefined, productTags, bundleOffersSummary, router.locale, {
        groupByRootCategory: true,
      }),
      null
    );

    allItems.forEach(el => {
      el.products.forEach(prod => {
        if (!prod.available) return;
        if (prod?.optionsGroups?.length > 0) {
          const optionProducts = initMyoProducts(prod, undefined);
          addProduct(
            { ...optionProducts[0], productMYO: false },
            false,
            undefined,
            true,
            t('asides.side_cart.no_comment')
          );
        } else {
          // Check for ingredient groups that have minimum requirements
          if (prod?.ingredientsGroups) {
            for (const group of prod.ingredientsGroups) {
              if (group.minOptionsAllowed >= 1) {
                // TODO: Add wording here
                openActionModal({
                  title: t('ingredients.alert.lists.title'),
                  text: t('ingredients.alert.lists.text'),
                  img: {
                    url: genericErrorImage,
                    ariaLabel: 'Generic error image',
                    size: 'large',
                  },
                  actions: [
                    {
                      btnVariant: 'filled',
                      btnLabel: t('ingredients.alert.lists.action'),
                      onClick: () => {
                        close();
                      },
                    },
                  ],
                });
                return;
              }
            }
          }
          addProduct(
            { ...prod, ...(prod.ingredientsGroups && { ingredients: [] }) },
            false,
            undefined,
            true,
            t('asides.side_cart.no_comment'),
            true
          );
        }
      });
    });
    updateCartOrder(productsForOrder.getValue(), 'none', false, t('asides.side_cart.no_comment'));
  };
  return (
    <>
      {isLoading ? (
        <ProductCatalogueV2 isLoading loaderTilesAmount={10} products={[]} />
      ) : groupedProducts.length > 0 ? (
        <div className={styles.columnContainer}>
          <WaveBreadCrumb LinkComponent={Link} items={breadCrumbItems} iconLink={'/'} />

          <div className={cn({ [styles.loading]: isLoading }, styles.ListCategoriesContainer)}>
            <ListProductCatalogueHeader
              hasListHeader={true}
              listTitle={selectedList.label}
              listDescription={
                numberOfProducts === 1
                  ? `${numberOfProducts} ${t1('product')}`
                  : `${numberOfProducts} ${t1('products')}`
              }
              buttonText={t1('add_all_to_cart')}
              addNewText={t1('add_all_to_cart')}
              onBack={() => {
                openMenu();
              }}
              onButtonClick={async () => {
                LoaderStore.setIsVisible(true);
                await fetchAllProducts();
                addProductsToCart();
                LoaderStore.setIsVisible(false);
              }}
            />
            {groupedProducts.map(i => {
              return (
                <div key={i.category + '-' + i.name} className={styles.catalogueRow}>
                  <ListsProductCatalogueV2
                    currencyFormatter={formatter}
                    title={i.category}
                    products={i.products}
                    noItemsFoundImage={image ? image : EmptyState.src}
                    noItemsFoundWording={t1('favourites_empty')}
                    noItemsFoundDescription={t1('favourites_empty_desc')}
                    addItemWording={t1('show_products')}
                    addNewText={t1('add_all_to_cart')}
                    onListClick={openListSideSheet}
                    checkStatus={checkListStatus}
                    addProduct={product =>
                      addProduct(
                        product,
                        false,
                        {
                          item_list_id: 'listCatalogue',
                          item_list_name: 'listCatalogue',
                        },
                        false,
                        t('asides.side_cart.no_comment')
                      )
                    }
                    removeProduct={product =>
                      removeProduct(
                        product,
                        {
                          item_list_id: 'listCatalogue',
                          item_list_name: 'listCatalogue',
                        },
                        t('asides.side_cart.no_comment')
                      )
                    }
                    ImageComponent={CustomImage}
                    checkProductAvailability={checkProductCollectionAndAvailability}
                    defaultQsText={t('qs.default')}
                    defaultOutOfStockText={t('asides.out_of_stock')}
                    defaultQsUnavailableText={t('qs.default_unavailable')}
                    LinkComponent={props => {
                      const propsWithCleanUrl = getProductPropsWithCleanUrl(props);

                      return (
                        <Link
                          prefetch={false}
                          {...propsWithCleanUrl}
                          onClick={() => {
                            const product = getProductBasedOnSlug(i.products, propsWithCleanUrl.href);

                            if (product) {
                              selectItem(getProductInfo(product?.originalValues), {
                                item_list_id: 'list_custom',
                                item_list_name: `List Custom`,
                                index: getProductIndexBasedOnSlug(propsWithCleanUrl.href) + 1,
                              });
                            }
                          }}
                        />
                      );
                    }}
                    offerLabelHasRibbon={process.env.offerLabelHasRibbon}
                    isTileDetailsAnimated={process.env.isTileDetailsAnimated}
                    ctaIcon={process.env.ctaIcon}
                    listIcon={process.env.listIcon}
                    lastItemRef={lastResultItemRef}
                    hasRoundedCorners={process.env.hasRoundedCorners}
                    defaultProductImage={defaultProductImage}
                    forYouLabel={t('user.profile.menu.for_you')}
                  />
                </div>
              );
            })}

            {isFetchingNextPage ? <ProductCatalogueV2 isLoading loaderTilesAmount={4} products={[]} /> : null}

            <SnackBar open={showSnackBar} setOpen={setShowSnackbar} text={t('snackbar')} position={'center'} />
          </div>
        </div>
      ) : (
        <div className={styles.fullColumnContainer}>
          <WaveBreadCrumb LinkComponent={Link} items={breadCrumbItems} iconLink={'/'} />
          <ListsProductCatalogueV2
            title={t1('favourites')}
            products={[]}
            noItemsFoundImage={image ? image : EmptyState.src}
            noItemsFoundWording={t1('favourites_empty')}
            noItemsFoundDescription={t1('favourites_empty_desc')}
            addItemWording={t1('show_products')}
            addNewText={t1('add_all_to_cart')}
            onListClick={openListSideSheet}
            checkStatus={checkListStatus}
            ImageComponent={CustomImage}
            onBack={() => {
              openMenu();
            }}
            addProduct={product =>
              addProduct(
                product,
                false,
                {
                  item_list_id: 'listCatalogue',
                  item_list_name: 'listCatalogue',
                },
                false,
                t('asides.side_cart.no_comment')
              )
            }
            removeProduct={product =>
              removeProduct(
                product,
                {
                  item_list_id: 'listCatalogue',
                  item_list_name: 'listCatalogue',
                },
                t('asides.side_cart.no_comment')
              )
            }
            onAdd={() => router.push('/categories')}
            checkProductAvailability={checkProductAvailability}
            LinkComponent={props => {
              const propsWithCleanUrl = getProductPropsWithCleanUrl(props);

              return (
                <Link
                  prefetch={false}
                  {...propsWithCleanUrl}
                  onClick={() => {
                    const product = getProductBasedOnSlug(i.products, propsWithCleanUrl.href);

                    if (product) {
                      selectItem(getProductInfo(product?.originalValues), {
                        item_list_id: 'list_favourites',
                        item_list_name: 'List Favorites',
                        index: getProductIndexBasedOnSlug(propsWithCleanUrl.href) + 1,
                      });
                    }
                  }}
                />
              );
            }}
            offerLabelHasRibbon={process.env.offerLabelHasRibbon}
            isTileDetailsAnimated={process.env.isTileDetailsAnimated}
            ctaIcon={process.env.ctaIcon}
            listIcon={process.env.listIcon}
            hasRoundedCorners={process.env.hasRoundedCorners}
            forYouLabel={t('user.profile.menu.for_you')}
          />
        </div>
      )}
      <OffersSnackbar
        emoji="🎉"
        open={showBundleOffersSnackBar}
        setOpen={BundleOfferSnackBarOpen.update}
        hasCloseOption={true}
        position="center"
        type="warning"
        autoWidth={true}
        buttonText={t('offers_snack_bar.btn_label')}
        text={getBundleOfferDescription(SelectedBundleOfferStore.getValue(), bundleOffersSummary)}
        onButtonPress={() => {
          setShowBundleOffersSnackBar(false);
          BundleOfferSnackBarOpen.update(false);
          BundleOfferSideSheetStore.update(true);
        }}
        autoHideDuration={8000}
        closeOnClickAway={true}
        hasShadow={true}
      />
    </>
  );
}
